import type { CaseAttention, StatusDisposition } from '@/shared/domain/status';

/** Visual weight of a status, not a status in itself. */
export type Tone = 'ready' | 'ai' | 'manual' | 'neutral' | 'unsafe' | 'clar' | 'conf' | 'signed';

/** Row emphasis for cases that need to stand out in a dense table. */
export type RowTone = 'flagged' | 'critical';

/* The one translation from the domain's semantic categories to this module's
 * visual vocabulary.
 *
 * `StatusDisposition` and `CaseAttention` (`shared/domain/status.ts`) are
 * business categories a selector or Pixie can compare directly; `Tone` and
 * `RowTone` are how loud that category is drawn. Keeping the translation here,
 * rather than letting the domain carry `Tone` itself, is what lets this file's
 * palette change — or Scale's tag colours move — without touching a single
 * business rule. */
const toneByDisposition: Readonly<Record<StatusDisposition, Tone>> = {
  ready: 'ready',
  automation: 'ai',
  manual: 'manual',
  neutral: 'neutral',
  blocked: 'unsafe',
  clarification: 'clar',
  conflict: 'conf',
  completed: 'signed',
};

/** The tone a semantic disposition is drawn in. The only place this mapping is
 *  made — a screen or table asks this rather than assuming a disposition's
 *  name lines up with a tone's. */
export function toneFor(disposition: StatusDisposition): Tone {
  return toneByDisposition[disposition];
}

const rowToneByAttention: Readonly<Record<CaseAttention, RowTone>> = {
  flagged: 'flagged',
  critical: 'critical',
};

/** The row tone a semantic attention level is drawn in. Exists for the same
 *  reason `toneFor` does, even though today's two attention levels and two row
 *  tones happen to share their names — a screen asks rather than assumes. */
export function rowToneFor(attention: CaseAttention): RowTone {
  return rowToneByAttention[attention];
}

/* One translation from the cockpit's tones to Scale's tag palette.
 *
 * Outside a table a tone is drawn by Pill, which uses our own tokens. Inside
 * Scale's data grid the cell is a scale-tag, which takes a colour from a fixed
 * list. The same status therefore has two renderings, and the only way they stay
 * saying the same thing is for the mapping to live in one place.
 *
 * Scale has no magenta tag, so `signed` — the brand colour on a Pill — becomes
 * teal rather than borrowing violet, which `manual` already owns. Telling two
 * states apart matters more here than matching the pill exactly.
 *
 * `black` is in scale-tag's colour union but has no rule in its stylesheet, so
 * it renders as the untinted default. `unsafe` therefore asks for that default
 * by name instead. */

export type TagColor =
  | 'cyan'
  | 'yellow'
  | 'green'
  | 'orange'
  | 'red'
  | 'violet'
  | 'brown'
  | 'olive'
  | 'teal'
  | 'standard';

const colors: Readonly<Record<Tone, TagColor>> = {
  ready: 'green',
  clar: 'yellow',
  conf: 'red',
  manual: 'violet',
  ai: 'cyan',
  signed: 'teal',
  unsafe: 'standard',
  neutral: 'standard',
};

export function tagColor(tone: Tone): TagColor {
  return colors[tone];
}
